import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  CameraAlt,
  Collections,
  RemoveCircleOutline,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { User } from '../../types';
import { ChildFormData, saveChild, useFamily } from './services';

const MAX_EMERGENCY_CONTACTS = 3;

const ChildForm: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { userData } = useAuth();
  console.log({ userData });
  const family = useFamily(userData?.familyId || '');
  const isEdit = !!childId;

  const [form, setForm] = useState<ChildFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    phone: '',
    profilePic: null,
    allergies: 'N/A',
    specialNotes: '',
    emergencyContacts: [],
  });
  const [dobText, setDobText] = useState('');
  const [dobError, setDobError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState<HTMLElement | null>(
    null
  );

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEdit) return;
    const child = family.find((m) => m.userId === childId) as User | undefined;
    if (!child) return;
    setForm({
      firstName: child.firstName ?? '',
      lastName: child.lastName ?? '',
      dateOfBirth: child.dateOfBirth ?? null,
      phone: child.phone ?? userData?.phone ?? '',
      profilePic: child.profilePic ?? null,
      allergies: child.allergies ?? 'N/A',
      specialNotes: child.specialNotes ?? '',
      emergencyContacts: child.emergencyContacts ?? [],
    });
    if (child.dateOfBirth) {
      const d = child.dateOfBirth;
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      setDobText(`${mm}/${dd}/${d.getFullYear()}`);
    }
  }, [family, childId, isEdit, userData?.phone]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, profilePic: URL.createObjectURL(file) }));
    e.target.value = '';
  };

  const updateContact = (
    index: number,
    field: 'name' | 'phone',
    value: string
  ) => {
    setForm((f) => {
      const updated = [...f.emergencyContacts];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, emergencyContacts: updated };
    });
  };

  const addContact = () => {
    setForm((f) => ({
      ...f,
      emergencyContacts: [...f.emergencyContacts, { name: '', phone: '' }],
    }));
  };

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(
        4
      )}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    setDobText(formatted);

    if (digits.length < 8) {
      setDobError('');
      setForm((f) => ({ ...f, dateOfBirth: null }));
      return;
    }

    const month = parseInt(digits.slice(0, 2), 10);
    const day = parseInt(digits.slice(2, 4), 10);
    const year = parseInt(digits.slice(4, 8), 10);
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      setDobError('Please enter a valid date.');
      setForm((f) => ({ ...f, dateOfBirth: null }));
      return;
    }

    const today = new Date();
    let age = today.getFullYear() - year;
    const m = today.getMonth() - (month - 1);
    if (m < 0 || (m === 0 && today.getDate() < day)) age--;
    if (age < 4 || age > 12) {
      setDobError('Child must be between 4 and 12 years old.');
      setForm((f) => ({ ...f, dateOfBirth: null }));
    } else {
      setDobError('');
      setForm((f) => ({ ...f, dateOfBirth: date }));
    }
  };

  const removeContact = (index: number) => {
    setForm((f) => ({
      ...f,
      emergencyContacts: f.emergencyContacts.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    if (!form.profilePic) {
      setError('A photo is required.');
      return;
    }
    if (!form.dateOfBirth || dobError) {
      setError(dobError || 'Date of birth is required.');
      return;
    }
    if (!userData?.familyId) {
      setError('Could not determine family. Please sign out and try again.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await saveChild(
        form,
        userData.familyId,
        userData.userId,
        userData.email,
        childId
      );
      navigate('/checkin/family');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 6 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/checkin/family')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6">
          {isEdit ? 'Edit Child' : 'Add Child'}
        </Typography>
      </Stack>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Photo */}
          <Stack alignItems="center" spacing={1}>
            <Avatar
              src={form.profilePic ?? undefined}
              sx={{ width: 100, height: 100, fontSize: 40 }}
            >
              {form.firstName?.[0]}
            </Avatar>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CameraAlt />}
              onClick={(e) => setPhotoMenuAnchor(e.currentTarget)}
            >
              {form.profilePic ? 'Change Photo' : 'Add Photo *'}
            </Button>

            {/* Hidden inputs */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
            />

            <Menu
              anchorEl={photoMenuAnchor}
              open={!!photoMenuAnchor}
              onClose={() => setPhotoMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setPhotoMenuAnchor(null);
                  cameraInputRef.current?.click();
                }}
              >
                <ListItemIcon>
                  <CameraAlt fontSize="small" />
                </ListItemIcon>
                <ListItemText>Take Photo</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setPhotoMenuAnchor(null);
                  galleryInputRef.current?.click();
                }}
              >
                <ListItemIcon>
                  <Collections fontSize="small" />
                </ListItemIcon>
                <ListItemText>Choose from Gallery</ListItemText>
              </MenuItem>
            </Menu>
          </Stack>

          <TextField
            label="First Name"
            value={form.firstName}
            onChange={(e) =>
              setForm((f) => ({ ...f, firstName: e.target.value }))
            }
            required
            fullWidth
          />
          <TextField
            label="Last Name"
            value={form.lastName}
            onChange={(e) =>
              setForm((f) => ({ ...f, lastName: e.target.value }))
            }
            required
            fullWidth
          />
          <TextField
            label="Date of Birth *"
            value={dobText}
            onChange={handleDobChange}
            placeholder="MM/DD/YYYY"
            inputProps={{ maxLength: 10 }}
            error={!!dobError}
            helperText={dobError || ' '}
            fullWidth
          />
          <TextField
            label="Parent's Phone Number"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            type="tel"
            inputProps={{ maxLength: 15 }}
            fullWidth
          />
          <TextField
            label="Allergies"
            value={form.allergies}
            onChange={(e) =>
              setForm((f) => ({ ...f, allergies: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
            placeholder="e.g. peanuts, dairy"
          />
          <TextField
            label="Special Notes"
            value={form.specialNotes}
            onChange={(e) =>
              setForm((f) => ({ ...f, specialNotes: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
          />

          {/* Emergency Contacts */}
          <Divider />
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle2">
                Emergency Contacts ({form.emergencyContacts.length}/
                {MAX_EMERGENCY_CONTACTS})
              </Typography>
              {form.emergencyContacts.length < MAX_EMERGENCY_CONTACTS && (
                <Button size="small" startIcon={<Add />} onClick={addContact}>
                  Add Contact
                </Button>
              )}
            </Stack>

            {form.emergencyContacts.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No emergency contacts added.
              </Typography>
            )}

            <Stack spacing={2}>
              {form.emergencyContacts.map((contact, i) => (
                <Box
                  key={i}
                  sx={{ border: '1px solid #ddd', borderRadius: 2, p: 1.5 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Contact {i + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeContact(i)}
                    >
                      <RemoveCircleOutline fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack spacing={1.5}>
                    <TextField
                      label="Name"
                      value={contact.name}
                      onChange={(e) => updateContact(i, 'name', e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Phone"
                      value={contact.phone}
                      onChange={(e) =>
                        updateContact(i, 'phone', e.target.value)
                      }
                      type="tel"
                      size="small"
                      inputProps={{ maxLength: 15 }}
                      fullWidth
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/checkin/family')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Container>
  );
};

export default ChildForm;
